import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CategoryToken, EnumToken, ProductFilterToken } from '@sleed/bizcommerce-api';
import { CustomDeleteBehavior } from '@sleed/bizcommerce-api';
import { categoriesApi, binaryRepoImagesApi, productFiltersApi } from '~/lib/api/client';
import { formToToken, type CategoryFormValues, type ProductSortTypeOption } from '~/lib/schemas/category';

export const categoryKeys = {
	all: ['categories'] as const,
	list: () => [...categoryKeys.all, 'list'] as const,
	detail: (id: number | string) => [...categoryKeys.all, 'detail', id] as const,
	sortTypes: () => [...categoryKeys.all, 'sort-types'] as const
};

/** Flat category list (used to build the tree). */
export function useCategoriesList() {
	return useQuery<CategoryToken[]>({
		queryKey: categoryKeys.list(),
		queryFn: async () => (await categoriesApi.getAll()).data.items ?? []
	});
}

/** Single category by id (full record). `create` → null. */
export function useCategory(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<CategoryToken | null>({
		queryKey: categoryKeys.detail(id),
		queryFn: async () => (enabled ? (await categoriesApi.getById(numericId)).data : null),
		enabled
	});
}

/** Product-sort-type options ({ id, nm }) for the sort-type dropdown. */
export function useProductSortTypes() {
	return useQuery<ProductSortTypeOption[]>({
		queryKey: categoryKeys.sortTypes(),
		queryFn: async () => {
			const items = (await categoriesApi.getProductSortTypes()).data.items ?? [];
			return items
				.filter((e: EnumToken): e is EnumToken & { id: number } => typeof e.id === 'number')
				.map(e => ({ id: e.id, nm: e.nm ?? String(e.id) }));
		},
		staleTime: 10 * 60 * 1000
	});
}

/** Product-filter lookup ({ id, Name }) for the dynamic-filter include/exclude multi-selects. */
export function useProductFiltersLookup() {
	return useQuery<ProductFilterToken[]>({
		queryKey: ['product-filters', 'lookup'] as const,
		queryFn: async () => (await productFiltersApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/**
 * Upload a category image into slot `key` ("1".."5"). The category must already exist (id > 0);
 * the File is attached as form-data keyed by the slot subkey (mirrors the legacy
 * BinaryRepoImages/UploadProductCategory contract and the manufacturer/status uploads).
 */
export async function uploadCategoryImage(categoryId: number, key: '1' | '2' | '3' | '4' | '5', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadProductCategory(categoryId, key, { data: fd });
}

export function useCategoryMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: categoryKeys.all });
	const create = useMutation({
		mutationFn: async (form: CategoryFormValues) => (await categoriesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: CategoryFormValues) => (await categoriesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async ({ id, behavior }: { id: number; behavior: CustomDeleteBehavior }) => (await categoriesApi.remove(id, behavior)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
